/**
 * Centralized Realtime Client for Supabase
 *
 * This module wraps Supabase Realtime setup to provide a clean, centralized
 * interface for realtime subscriptions across the application.
 *
 * Key Principles:
 * - Always scope subscriptions by school_id for RLS compliance
 * - No UI or business logic - pure infrastructure
 * - Typed helpers for domain-specific channels
 * - Clean unsubscribe on teardown
 *
 * Usage:
 * ```typescript
 * const unsubscribe = subscribeToAttendanceEvents({
 *   schoolId: 'school-uuid',
 *   onChange: () => {
 *     // Invalidate RTK Query cache
 *   }
 * });
 *
 * // Later:
 * unsubscribe();
 * ```
 */

import { supabase } from "@flexwise/shared/lib/supabaseClient";
import type {
  RealtimeChannel,
  RealtimePostgresChangesPayload,
} from "@supabase/supabase-js";

/**
 * Attendance event types for realtime subscriptions
 */
export type AttendanceEventType =
  | "student_attendance_logs"
  | "student_absence_notes"
  | "student_absences";

/**
 * Configuration for attendance channel subscription
 */
export interface AttendanceChannelConfig {
  schoolId: string;
  event?: "INSERT" | "UPDATE" | "DELETE" | "*";
  table?: AttendanceEventType;
}

/**
 * Callback for attendance change events
 */
export type AttendanceChangeCallback = (
  payload: RealtimePostgresChangesPayload<any>,
) => void;

/**
 * Subscription options for attendance events
 */
export interface AttendanceSubscriptionOptions {
  schoolId: string;
  onChange: AttendanceChangeCallback;
  onError?: (error: Error) => void;
  tables?: AttendanceEventType[];
}

/**
 * Create a channel for attendance-related realtime updates
 *
 * This function creates a Supabase channel scoped to a specific school.
 * The channel is NOT automatically subscribed - use subscribeToAttendanceEvents
 * for a higher-level interface.
 *
 * @param config - Channel configuration with school_id
 * @returns Realtime channel instance
 */
export function createAttendanceChannel(
  config: AttendanceChannelConfig,
): RealtimeChannel {
  const { schoolId } = config;

  if (!schoolId) {
    throw new Error("schoolId is required for attendance channel");
  }

  // Create a unique channel name for this school
  const channelName = `attendance:school:${schoolId}`;

  return supabase.channel(channelName);
}

/**
 * Subscribe to attendance-related database changes
 *
 * This function sets up realtime listeners for attendance data changes
 * scoped to a specific school. It listens to:
 * - student_attendance_logs (check-ins/outs)
 * - student_absence_notes (absence records)
 * - student_absences (absence data)
 *
 * All events are filtered by school_id to ensure RLS compliance and
 * multi-tenancy isolation.
 *
 * @param options - Subscription options including schoolId and callbacks
 * @returns Unsubscribe function to clean up the subscription
 *
 * @example
 * ```typescript
 * const unsubscribe = subscribeToAttendanceEvents({
 *   schoolId: schoolId,
 *   onChange: () => {
 *     // Invalidate RTK Query cache
 *     dispatch(attendanceApi.util.invalidateTags(['Attendance']));
 *   }
 * });
 *
 * // Clean up on unmount
 * return () => unsubscribe();
 * ```
 */
export function subscribeToAttendanceEvents(
  options: AttendanceSubscriptionOptions,
): () => void {
  const {
    schoolId,
    onChange,
    onError,
    tables = [
      "student_attendance_logs",
      "student_absence_notes",
      "student_absences",
    ],
  } = options;

  if (!schoolId) {
    throw new Error("schoolId is required for attendance subscriptions");
  }

  // Create channel
  const channel = createAttendanceChannel({ schoolId });

  // Subscribe to each table with school_id filter
  tables.forEach((table) => {
    channel.on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table,
        filter: `school_id=eq.${schoolId}`,
      },
      (payload) => {
        try {
          console.log(
            `[RealtimeClient] ${table} change detected:`,
            payload.eventType,
          );
          onChange(payload);
        } catch (error) {
          console.error(
            `[RealtimeClient] Error handling ${table} change:`,
            error,
          );
          if (onError) {
            onError(error as Error);
          }
        }
      },
    );
  });

  // Subscribe to the channel
  channel.subscribe((status) => {
    if (status === "SUBSCRIBED") {
      console.log(
        `[RealtimeClient] Subscribed to attendance updates for school ${schoolId}`,
      );
    } else if (status === "CHANNEL_ERROR") {
      console.error("[RealtimeClient] Channel error occurred");
      if (onError) {
        onError(new Error("Realtime channel error"));
      }
    } else if (status === "TIMED_OUT") {
      console.error("[RealtimeClient] Channel subscription timed out");
      if (onError) {
        onError(new Error("Realtime channel timeout"));
      }
    }
  });

  // Return unsubscribe function
  return () => {
    console.log(
      `[RealtimeClient] Unsubscribing from attendance updates for school ${schoolId}`,
    );
    supabase.removeChannel(channel);
  };
}

/**
 * Substitution event types for realtime subscriptions
 */
export type SubstitutionEventType =
  | "class_absences"
  | "staff_absences"
  | "substitutions"
  | "aufsichten_substitutions";

/**
 * Configuration for substitution channel subscription
 */
export interface SubstitutionChannelConfig {
  schoolId: string;
  event?: "INSERT" | "UPDATE" | "DELETE" | "*";
  table?: SubstitutionEventType;
}

/**
 * Callback for substitution change events
 */
export type SubstitutionChangeCallback = (
  payload: RealtimePostgresChangesPayload<any>,
) => void;

/**
 * Subscription options for substitution events
 */
export interface SubstitutionSubscriptionOptions {
  schoolId: string;
  onChange: SubstitutionChangeCallback;
  onError?: (error: Error) => void;
  tables?: SubstitutionEventType[];
}

/**
 * Create a channel for substitution-related realtime updates
 *
 * @param config - Channel configuration with school_id
 * @returns Realtime channel instance
 */
export function createSubstitutionChannel(
  config: SubstitutionChannelConfig,
): RealtimeChannel {
  const { schoolId } = config;

  if (!schoolId) {
    throw new Error("schoolId is required for substitution channel");
  }

  // Create a unique channel name for this school
  const channelName = `substitution:school:${schoolId}`;

  return supabase.channel(channelName);
}

/**
 * Subscribe to substitution-related database changes
 *
 * This function sets up realtime listeners for substitution data changes
 * scoped to a specific school. It listens to:
 * - class_absences (class absence records)
 * - staff_absences (staff absence records)
 * - substitutions (substitution assignments)
 *
 * All events are filtered by school_id to ensure RLS compliance and
 * multi-tenancy isolation.
 *
 * @param options - Subscription options including schoolId and callbacks
 * @returns Unsubscribe function to clean up the subscription
 *
 * @example
 * ```typescript
 * const unsubscribe = subscribeToSubstitutionEvents({
 *   schoolId: schoolId,
 *   onChange: () => {
 *     // Invalidate RTK Query cache
 *     dispatch(substitutionApi.util.invalidateTags(['Substitution']));
 *   }
 * });
 *
 * // Clean up on unmount
 * return () => unsubscribe();
 * ```
 */
export function subscribeToSubstitutionEvents(
  options: SubstitutionSubscriptionOptions,
): () => void {
  const {
    schoolId,
    onChange,
    onError,
    tables = [
      "class_absences",
      "staff_absences",
      "substitutions",
      "aufsichten_substitutions",
    ],
  } = options;

  if (!schoolId) {
    throw new Error("schoolId is required for substitution subscriptions");
  }

  // Create channel
  const channel = createSubstitutionChannel({ schoolId });

  // Subscribe to each table with school_id filter
  tables.forEach((table) => {
    channel.on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table,
        filter: `school_id=eq.${schoolId}`,
      },
      (payload) => {
        try {
          console.log(
            `[RealtimeClient] ${table} change detected:`,
            payload.eventType,
          );
          onChange(payload);
        } catch (error) {
          console.error(
            `[RealtimeClient] Error handling ${table} change:`,
            error,
          );
          if (onError) {
            onError(error as Error);
          }
        }
      },
    );
  });

  // Subscribe to the channel
  channel.subscribe((status) => {
    if (status === "SUBSCRIBED") {
      console.log(
        `[RealtimeClient] Subscribed to substitution updates for school ${schoolId}`,
      );
    } else if (status === "CHANNEL_ERROR") {
      console.error("[RealtimeClient] Channel error occurred");
      if (onError) {
        onError(new Error("Realtime channel error"));
      }
    } else if (status === "TIMED_OUT") {
      console.error("[RealtimeClient] Channel subscription timed out");
      if (onError) {
        onError(new Error("Realtime channel timeout"));
      }
    }
  });

  // Return unsubscribe function
  return () => {
    console.log(
      `[RealtimeClient] Unsubscribing from substitution updates for school ${schoolId}`,
    );
    supabase.removeChannel(channel);
  };
}

